import React from 'react';
import { 
  ExclamationTriangleIcon, 
  ShieldCheckIcon,
  InformationCircleIcon 
} from '@heroicons/react/24/outline';

interface RiskAssessmentProps {
  risks: string[];
}

const RiskAssessment: React.FC<RiskAssessmentProps> = ({ risks }) => {
  // Default risks if none provided
  const defaultRisks = [
    'Resistenza al cambiamento interno',
    'Integrazione con sistemi legacy',
    'Qualità dati insufficiente',
    'Budget overrun',
    'Timeline delays'
  ];

  const riskList = risks && risks.length > 0 ? risks : defaultRisks;

  const getRiskLevel = (risk: string) => {
    // Simple risk categorization based on keywords
    if (risk.toLowerCase().includes('budget') || risk.toLowerCase().includes('costo')) {
      return { level: 'high', color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200' };
    }
    if (risk.toLowerCase().includes('integrazione') || risk.toLowerCase().includes('legacy')) {
      return { level: 'medium', color: 'text-yellow-600', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200' };
    }
    if (risk.toLowerCase().includes('dati') || risk.toLowerCase().includes('qualità')) {
      return { level: 'medium', color: 'text-yellow-600', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200' };
    }
    return { level: 'low', color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-200' };
  };

  const getRiskLevelText = (level: string) => {
    switch (level) {
      case 'high':
        return 'Alto';
      case 'medium':
        return 'Medio';
      case 'low':
        return 'Basso';
      default:
        return 'Medio';
    }
  };

  const getMitigationStrategy = (risk: string) => {
    const strategies: { [key: string]: string } = {
      'resistenza al cambiamento': 'Workshop formativi e coinvolgimento stakeholder chiave',
      'integrazione sistemi': 'Analisi approfondita API e test di integrazione graduali',
      'qualità dati': 'Audit dati preliminare e processo di data cleaning',
      'budget': 'Monitoraggio costi in tempo reale e milestone di controllo',
      'timeline': 'Planning dettagliato con buffer temporali e checkpoint settimanali'
    };

    const lowerRisk = risk.toLowerCase();
    for (const [key, strategy] of Object.entries(strategies)) {
      if (lowerRisk.includes(key)) {
        return strategy;
      }
    }
    return 'Monitoraggio continuo e azioni correttive tempestive';
  };

  const riskCounts = {
    high: riskList.filter(risk => getRiskLevel(risk).level === 'high').length,
    medium: riskList.filter(risk => getRiskLevel(risk).level === 'medium').length,
    low: riskList.filter(risk => getRiskLevel(risk).level === 'low').length,
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center mb-6">
        <ExclamationTriangleIcon className="h-6 w-6 text-orange-600 mr-2" />
        <h2 className="text-xl font-bold text-gray-900">Risk Assessment</h2>
      </div>
      
      {/* Risk summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-2xl font-bold text-red-600">{riskCounts.high}</div>
          <div className="text-sm text-red-700">Rischi Alti</div>
        </div>
        <div className="text-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="text-2xl font-bold text-yellow-600">{riskCounts.medium}</div>
          <div className="text-sm text-yellow-700">Rischi Medi</div>
        </div>
        <div className="text-center p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="text-2xl font-bold text-green-600">{riskCounts.low}</div>
          <div className="text-sm text-green-700">Rischi Bassi</div>
        </div>
      </div>

      {/* Risk list */}
      <div className="space-y-4">
        {riskList.map((risk, index) => {
          const riskInfo = getRiskLevel(risk);
          return (
            <div key={index} className={`border rounded-lg p-4 ${riskInfo.bgColor} ${riskInfo.borderColor}`}>
              <div className="flex items-start justify-between">
                <div className="flex-grow">
                  <div className="flex items-center mb-2">
                    <ExclamationTriangleIcon className={`h-4 w-4 mr-2 ${riskInfo.color}`} />
                    <span className="font-medium text-gray-900">{risk}</span>
                  </div>
                  
                  <div className="ml-6">
                    <div className="flex items-start">
                      <ShieldCheckIcon className="h-4 w-4 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-1">Strategia di Mitigazione:</div>
                        <p className="text-sm text-gray-600">{getMitigationStrategy(risk)}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <span className={`ml-4 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${riskInfo.color} ${riskInfo.bgColor} border ${riskInfo.borderColor}`}>
                  {getRiskLevelText(riskInfo.level)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Risk management note */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-start p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-blue-800 mb-1">
              Gestione Proattiva dei Rischi
            </div>
            <p className="text-sm text-blue-700">
              Tutti i rischi identificati verranno monitorati settimanalmente durante l'implementazione. 
              Ogni rischio ha strategie di mitigazione specifiche e piani di contingenza pronti all'uso.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskAssessment;